<template>
  <div class="sidebar-content">
    <!-- avatar block -->
    <profile-avatar
      class="profile-avatar"
      :height="220"
      :avatar-id="sidebarInfo.avatar_id"
      :nickname="nickname"
      :gender="sidebarInfo.gender"
    />
    <!-- user information block -->
    <div class="profile-user-info">
      <sidebar-nickname :nickname="nickname"
                        :color="sidebarInfo.nickname_color"
                        :alevel="sidebarInfo.alevel"
      />
      <div class="user-info">
        <div class="user-info-status" :class="userStatus" />
        <div class="user-info-age">
          {{ sidebarInfo.age }}
        </div>
        <div class="user-info-gender">
          {{ $t(userGender) }}
        </div>
        <div class="user-info-country">
          {{ sidebarInfo.country }}
        </div>
      </div>
    </div>
    <!-- statistics block -->
    <div class="profile-statistic">
      <sidebar-info-item
        header-text="sidebar.subscription"
        button-text="sidebar.btn_upgrade"
        @more-info="toSubscriptionInfo"
      >
        <subscription-badge :type="sidebarInfo.subscription_type" />
      </sidebar-info-item>

      <sidebar-info-item
        header-text="sidebar.level"
        button-text="sidebar.btn_upgrade"
        @more-info="toXPInfo"
      >
        <image-a-level :alevel="sidebarInfo.alevel" type="web" />
        <span>{{ xpCount }}</span>
      </sidebar-info-item>

      <sidebar-info-item
        class="sidebar-info-coins"
        header-text="sidebar.coins_balance"
        button-text="sidebar.btn_buy_coins"
        @more-info="toCoinsPurchase"
      >
        <img src="@/assets/images/profiles/ic_coins-balance.svg" alt="balance">
        <span>{{ coinsAmount }}</span>
        <Button class="p-button-link autocharge-link" @click="toAutochargeInfo">
          {{ $t('sidebar.link_autocharge') }}
        </Button>
      </sidebar-info-item>

      <sidebar-info-item
        header-text="sidebar.notifications"
        button-text="sidebar.btn_view"
        @more-info="toNotificationsInfo"
      >
        <img src="@/assets/images/profiles/ic_notifications.svg" alt="notifications">
        <div v-if="notificationsCount > 0" class="new-notification-icon" />
        <span>{{ notificationsCount }}</span>
      </sidebar-info-item>

      <sidebar-info-item
        header-text="sidebar.reward_points"
        button-text="sidebar.btn_history"
        @more-info="toRewardHistory"
      >
        <img src="@/assets/images/profiles/ic_rewards-points.svg" alt="points">
        <span v-if="rewardPoints">{{ rewardPoints }}</span>
      </sidebar-info-item>
    </div>
    <!-- links block -->
    <div class="profile-links">
      <a class="profile-links-item" :href="profileUrl + '/settings.php#tab-information'">
        <img src="@/assets/images/profiles/ic_link_edit.svg" alt="edit">
        <span> {{ $t('sidebar.link_edit_setting') }}</span>
      </a>
      <a class="profile-links-item" :href="profileUrl + '/home.php#profile_visits'">
        <img src="@/assets/images/profiles/ic_link_history.svg" alt="history">
        <span> {{ $t('sidebar.link_visit_history') }}</span>
      </a>
      <a class="profile-links-item" :href="profileUrl + '/' + sidebarInfo.nickname">
        <img src="@/assets/images/profiles/ic_link_preview.svg" alt="preview">
        <span> {{ $t('sidebar.link_public_profile') }}</span>
      </a>
      <a class="profile-links-item" :href="profileUrl + '/home.php#share'">
        <img src="@/assets/images/profiles/ic_link_share.svg" alt="share">
        <span> {{ $t('sidebar.link_share_profile') }}</span>
      </a>
      <a class="profile-links-item" :href="baseUrl + '/about_rewards.php'">
        <img src="@/assets/images/profiles/ic_link_points.svg" alt="points">
        <span> {{ $t('sidebar.link_reward_points') }}</span>
      </a>
      <a class="profile-links-item" :href="profileUrl + '/settings.php#tab-room'">
        <img src="@/assets/images/profiles/ic_link_settings.svg" alt="settings">
        <span> {{ $t('sidebar.link_room_profile') }}</span>
      </a>
    </div>
  </div>
</template>

<script>
import * as Formatter from "@/services/number_formatter";
import { decodeNickname, getGenderName } from "@/services/helpers";
import ProfileAvatar from "@/profiles/components/ProfileAvatar";
import SidebarInfoItem from "./ProfileSidebar/SidebarInfoItem";
import ImageALevel from "@/components/ImageALevel";
import SidebarNickname from "@/profiles/components/ProfileSidebar/SidebarNickname";
import SubscriptionBadge from "@/profiles/components/ProfileSidebar/SubscriptionBadge";

export default {
  name: "ProfileSidebar",

  components: {
    SubscriptionBadge,
    SidebarNickname,
    SidebarInfoItem,
    ProfileAvatar,
    ImageALevel
  },

  props: {
    sidebarInfo: {
      type: Object,
      default: () => {}
    }
  },

  computed: {
    profileUrl() {
      return process.env.VUE_APP_PROFILES_BASE_URL
    },
    baseUrl() {
      return process.env.VUE_APP_BASE_URL
    },
    userStatus() {
      return [
        this.sidebarInfo.client_status,
        { privacy : this.sidebarInfo.privacy }
      ];
    },
    userGender() {
      return getGenderName(this.sidebarInfo.gender)
    },
    coinsAmount() {
      return Formatter.localeFormat(this.sidebarInfo.coins_amount)
    },
    notificationsCount() {
      return Formatter.localeFormat(this.sidebarInfo.notifications_count)
    },
    rewardPoints() {
      return Formatter.rewardPointsFormat(this.sidebarInfo.reward_program.balance)
    },
    xpCount() {
      return Formatter.localeFormat(this.sidebarInfo.xp_count)
    },
    nickname() {
      return decodeNickname(this.sidebarInfo.nickname)
    }
  },

  methods: {
    openOldProfile(tab) {
      window.location = `${this.profileUrl}/home.php#${tab}`
    },

    toSubscriptionInfo() {
      this.openOldProfile('subscription')
    },

    toXPInfo() {
      this.openOldProfile('profile_status')
    },

    toRewardHistory() {
      this.openOldProfile('rewards_history')
    },

    toAutochargeInfo() {
      this.openOldProfile('autocharge')
    },

    toNotificationsInfo() {
      this.$router.push('notifications')
    },

    toCoinsPurchase() {
      window.location = `${process.env.VUE_APP_BASE_URL}/coins`
    },

  }
}
</script>

<style scoped lang="scss">
.sidebar-content {
  height: 100%;
  position: relative;
  padding-top: 180px;
  .profile-avatar {
    position: absolute;
    left: 40px;
    top: -72px;
  }
  .profile-user-info {
    margin-bottom: 25px;

    .user-info {
      display: flex;
      justify-content: center;
      align-items: center;
      margin-top: 6px;
      font-weight: 400;
      font-size: 13px;
      line-height: 18px;
      color: #808080;
      &-status {
        width: 14px;
        height: 14px;
        margin-right: 7px;
        border-radius: 50%;
        background-color: #E2E2E2;

        &.online {
          background-color: #42C855;
        }
        &.offline {
          background-color: #E2E2E2;
        }
        &.away {
          background-color: #fec725;
        }
        &.busy {
          background-color: #ee616d;
        }
        &.privacy {
          background-image: url("~@/assets/images/icons/ic_privacy.svg");
          background-position: center;
        }
      }
      &-gender {
        position: relative;
        margin: 0 15px;
        &:after,
        &:before {
          content: "";
          width: 3px;
          height: 3px;
          position: absolute;
          top: 8px;
          border-radius: 50%;
          background: #808080;
        }
        &:after {
          right: -8px;
        }
        &:before {
          left: -8px;
        }
      }
    }
  }
  .profile-statistic {
    padding: 0 26px;
    border-bottom: 1px solid #E9E9E9;
  }
  .profile-links {
    padding: 27px;
    .profile-links-item {
      display: flex;
      align-items: center;
      position: relative;
      margin-bottom: 20px;
      padding-left: 39px;
      text-decoration:none;
      &:last-child {
        margin-bottom: 0;
      }
      &:hover {
        cursor: pointer;
        span {
          color: #3cafff;
        }
      }
      span {
        margin-left: 8px;
        font-weight: 400;
        font-size: 12px;
        line-height: 16px;
        color: #3D95CC;
      }
      img {
        position: absolute;
        left: 0;
      }
    }

  }

  .autocharge-link {
    position: absolute;
    color: #3d95cc;
    font-size: 10px;
    line-height: 14px;
    -webkit-transition: 0.2s;
    transition: 0.2s;
    top: 0;
    right: 0;
    padding: 5px 0;

    &:hover {
      color: #3cafff;
    }

    &::before {
      content: '+';
      display: flex;
      justify-content: center;
      align-items: center;
      height: 11px;
      width: 11px;
      border: 1px solid currentColor;
      border-radius: 50%;
      font-weight: 400;
      font-size: 12px;
      line-height: 9px;
      transition: .2s;
      margin-right: 4px;
    }
  }
  .new-notification-icon {
    width: 10px;
    height: 10px;
    position: relative;
    left: -3px;
    top: 3px;
    border-radius: 50%;
    background: #FFF;

    &:before {
      content: '';
      width: 8px;
      height: 8px;
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: #F0251E;
      border-radius: 50%;
    }
  }
  @media screen and (max-width: 740px) {
    .profile-avatar {
      left: 50%;
      transform: translate(-50%);
    }
  }
}

</style>
